// Sanidad (planes, eventos, calendario, retiros). Ganadería → Sanidad. Fase 2A (667).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Plus, Pencil, ShieldAlert, Stethoscope, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Animal, BaseCalculo, Campo, Lote, Modal, PlanItem, PlanSanitario, SanidadEvento, SanidadPendiente,
  SanidadRetiro, SortTh, Switch, TIPOS_PLAN_ITEM, TipoPlanItem, VIAS_APLICACION, esAftosa, fieldCls,
  ganRpc, hoyISO, invalidateGanaderia, num, tipoPlanItemLabel, useSort,
} from './shared';

type SubTab = 'pendientes' | 'retiros' | 'eventos' | 'planes';

interface PrefillEvento { animalId?: string; tipo?: string; nombreEvento?: string }

export function Sanidad() {
  const [tab, setTab] = useState<SubTab>('pendientes');
  const [modalEvento, setModalEvento] = useState<PrefillEvento | null>(null);
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const admin = isAdmin();

  const tabBtn = (t: SubTab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Stethoscope className="w-5 h-5" /> Sanidad</h1>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {tabBtn('pendientes', 'Calendario / Pendientes')}
        {tabBtn('retiros', 'Retiros vigentes')}
        {tabBtn('eventos', 'Eventos')}
        {tabBtn('planes', 'Planes')}
      </div>

      {tab === 'pendientes' && (
        <PendientesTab admin={admin} onRegistrar={(p) => setModalEvento({ animalId: p.animal_id, tipo: p.tipo, nombreEvento: p.item })} />
      )}
      {tab === 'retiros' && <RetirosTab />}
      {tab === 'eventos' && <EventosTab admin={admin} onRegistrar={() => setModalEvento({})} />}
      {tab === 'planes' && <PlanesTab admin={admin} />}

      {modalEvento && (
        <RegistrarSanidadModal
          prefill={modalEvento}
          onClose={() => setModalEvento(null)}
          onSaved={() => invalidateGanaderia(qc)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------- Pendientes/calendario
function PendientesTab({ admin, onRegistrar }: { admin: boolean; onRegistrar: (p: SanidadPendiente) => void }) {
  const { data = [], isFetching } = useQuery<SanidadPendiente[]>({
    queryKey: ['gan-sanidad-pendientes'], queryFn: () => ganRpc<SanidadPendiente[]>('gan_sanidad_pendientes'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const vencidas = data.filter((d) => d.estado === 'vencida').length;
  const proximas = data.filter((d) => d.estado === 'proxima').length;

  const getVal = useCallback((r: SanidadPendiente, key: string): string | number | null => {
    switch (key) {
      case 'arete': return r.arete;
      case 'nombre_animal': return r.nombre_animal;
      case 'lote_nombre': return r.lote_nombre;
      case 'item': return r.item;
      case 'tipo': return tipoPlanItemLabel(r.tipo);
      case 'fecha_venc': return r.fecha_venc;
      case 'dias': return r.dias;
      case 'estado': return r.estado;
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal, 'fecha_venc', 'asc');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Card className="flex-1 min-w-[160px]">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Vencidas</div>
            <div className="text-3xl font-semibold font-mono tabular-nums text-urgente mt-1">{vencidas}</div>
          </CardContent>
        </Card>
        <Card className="flex-1 min-w-[160px]">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Próximas</div>
            <div className="text-3xl font-semibold font-mono tabular-nums text-pendiente mt-1">{proximas}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Calendario sanitario ({data.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="arete" sort={sort}>Arete</SortTh>
              <SortTh col="nombre_animal" sort={sort}>Nombre</SortTh>
              <SortTh col="lote_nombre" sort={sort}>Lote</SortTh>
              <SortTh col="item" sort={sort}>Ítem</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="fecha_venc" sort={sort}>Vence</SortTh>
              <SortTh col="dias" sort={sort} align="right">Días</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin pendientes: el calendario sanitario está al día</td></tr>}
              {sort.sorted.map((p, i) => {
                const vencida = p.estado === 'vencida';
                return (
                  <tr key={`${p.animal_id}-${p.item}-${i}`} className={`border-b last:border-0 ${vencida ? 'bg-urgente-soft/40' : 'bg-pendiente-soft/30'}`}>
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{p.arete}</td>
                    <td className="py-2 pr-3">{p.nombre_animal ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.lote_nombre ?? '—'}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1">
                        {p.item}
                        {esAftosa(p.item) && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-urgente-soft text-urgente px-1.5 py-0.5 text-[10px] font-semibold" title="Vacuna obligatoria nacional">
                            <ShieldAlert className="w-3 h-3" /> AFTOSA
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{tipoPlanItemLabel(p.tipo)}{p.obligatoria ? ' *' : ''}</td>
                    <td className="py-2 pr-3">{p.fecha_venc}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{p.dias}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${vencida ? 'bg-urgente-soft text-urgente' : 'bg-pendiente-soft text-pendiente'}`}>
                        {vencida ? 'Vencida' : 'Próxima'}
                      </span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" disabled={!admin} onClick={() => onRegistrar(p)}>Registrar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.some((d) => d.obligatoria) && <p className="mt-2 text-xs text-muted-foreground">* ítem obligatorio del plan sanitario.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------------------------------------- Retiros vigentes
function RetirosTab() {
  const { data = [], isFetching } = useQuery<SanidadRetiro[]>({
    queryKey: ['gan-sanidad-retiros'], queryFn: () => ganRpc<SanidadRetiro[]>('gan_sanidad_retiros'),
    staleTime: 0, refetchOnMount: 'always',
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-urgente" /> Retiros vigentes ({data.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto space-y-2">
        <p className="text-sm text-muted-foreground">Estos animales tienen un período de retiro activo: no deben ir a leche ni a faena antes de la fecha indicada.</p>
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Arete</th><th className="py-2 pr-3">Nombre</th>
            <th className="py-2 pr-3">Retiro leche hasta</th><th className="py-2 pr-3">Retiro carne hasta</th>
          </tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin retiros vigentes hoy</td></tr>}
            {data.map((r, i) => (
              <tr key={r.animal_id} className="border-b last:border-0 bg-urgente-soft/20">
                <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3 font-medium">{r.arete}</td>
                <td className="py-2 pr-3">{r.nombre ?? '—'}</td>
                <td className="py-2 pr-3">{r.retiro_leche_hasta ?? '—'}</td>
                <td className="py-2 pr-3">{r.retiro_carne_hasta ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ Eventos
function EventosTab({ admin, onRegistrar }: { admin: boolean; onRegistrar: () => void }) {
  const [animalId, setAnimalId] = useState('');
  const [tipo, setTipo] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const { data = [], isFetching } = useQuery<SanidadEvento[]>({
    queryKey: ['gan-sanidad-eventos', animalId, tipo, desde, hasta],
    queryFn: () => ganRpc<SanidadEvento[]>('gan_sanidad_evento_listar', {
      _animal: animalId || null, _tipo: tipo || null, _desde: desde || null, _hasta: hasta || null,
    }),
  });

  const getVal = useCallback((r: SanidadEvento, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'arete': return r.arete;
      case 'nombre': return r.nombre;
      case 'tipo': return tipoPlanItemLabel(r.tipo);
      case 'nombre_evento': return r.nombre_evento;
      case 'producto': return r.producto;
      case 'responsable': return r.responsable;
      case 'costo': return r.costo;
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Animal" className="w-64">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">Todos</option>
              {animales.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
          <Campo label="Tipo" className="w-48">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS_PLAN_ITEM.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Button className="ml-auto" disabled={!admin} onClick={onRegistrar}><Plus className="w-4 h-4 mr-1" /> Registrar evento</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Eventos sanitarios ({data.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="arete" sort={sort}>Arete</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="nombre_evento" sort={sort}>Evento</SortTh>
              <SortTh col="producto" sort={sort}>Producto</SortTh>
              <th className="py-2 pr-3">Vía</th>
              <SortTh col="responsable" sort={sort}>Responsable</SortTh>
              <th className="py-2 pr-3">Retiro leche</th>
              <th className="py-2 pr-3">Retiro carne</th>
              <SortTh col="costo" sort={sort} align="right">Costo</SortTh>
              <th className="py-2 pr-3">Próx. refuerzo</th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-muted-foreground">Sin eventos registrados</td></tr>}
              {sort.sorted.map((e, i) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{e.fecha}</td>
                  <td className="py-2 pr-3 font-medium">{e.arete}</td>
                  <td className="py-2 pr-3">{e.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{tipoPlanItemLabel(e.tipo)}</td>
                  <td className="py-2 pr-3">{e.nombre_evento}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.producto ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.via ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.responsable ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.retiro_leche_hasta ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.retiro_carne_hasta ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.costo != null ? num(e.costo, 2) : '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.proximo_refuerzo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------- Registrar evento (modal)
function RegistrarSanidadModal({ prefill, onClose, onSaved }: { prefill: PrefillEvento; onClose: () => void; onSaved: () => void }) {
  const [destino, setDestino] = useState<'animal' | 'lote'>('animal');
  const [animalId, setAnimalId] = useState(prefill.animalId ?? '');
  const [loteId, setLoteId] = useState('');
  const [tipo, setTipo] = useState<TipoPlanItem>((prefill.tipo as TipoPlanItem) ?? 'vacuna');
  const [nombre, setNombre] = useState(prefill.nombreEvento ?? '');
  const [producto, setProducto] = useState('');
  const [dosis, setDosis] = useState('');
  const [via, setVia] = useState('');
  const [loteProducto, setLoteProducto] = useState('');
  const [responsable, setResponsable] = useState('');
  const [planItemId, setPlanItemId] = useState('');
  const [diagnostico, setDiagnostico] = useState('');
  const [retiroLeche, setRetiroLeche] = useState('');
  const [retiroCarne, setRetiroCarne] = useState('');
  const [costo, setCosto] = useState('');
  const [proximoRefuerzo, setProximoRefuerzo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fecha, setFecha] = useState(hoyISO());

  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });
  const { data: planes = [] } = useQuery<PlanSanitario[]>({ queryKey: ['gan-planes-sanitarios'], queryFn: () => ganRpc<PlanSanitario[]>('gan_plan_sanitario_listar') });
  const itemsActivos = useMemo(
    () => planes.filter((p) => p.activo).flatMap((p) => p.items.map((it) => ({ ...it, planNombre: p.nombre }))),
    [planes],
  );

  const registrar = useMutation({
    mutationFn: () => ganRpc<{ ok: boolean; creados: number }>('gan_sanidad_registrar', {
      _data: {
        animal_id: destino === 'animal' ? (animalId || null) : null,
        lote_id: destino === 'lote' ? (loteId || null) : null,
        tipo, nombre, producto: producto || null, dosis: dosis || null, via: via || null,
        lote_producto: loteProducto || null, responsable: responsable || null,
        plan_item_id: planItemId || null, diagnostico: diagnostico || null,
        retiro_leche_dias: retiroLeche === '' ? null : Number(retiroLeche),
        retiro_carne_dias: retiroCarne === '' ? null : Number(retiroCarne),
        costo: costo === '' ? null : Number(costo),
        proximo_refuerzo: proximoRefuerzo || null,
        observaciones: observaciones || null, fecha,
      },
    }),
    onSuccess: (r) => {
      toast.success(destino === 'lote' ? `Evento registrado para ${r.creados} animal(es) del lote` : 'Evento registrado');
      onSaved(); onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = nombre.trim() !== '' && (destino === 'animal' ? !!animalId : !!loteId);

  return (
    <Modal title="Registrar evento sanitario" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Destino *">
            <select className={fieldCls} value={destino} onChange={(e) => setDestino(e.target.value as 'animal' | 'lote')}>
              <option value="animal">Animal individual</option>
              <option value="lote">Lote entero (campaña)</option>
            </select>
          </Campo>
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
        </div>
        {destino === 'animal' ? (
          <Campo label="Animal *">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {animales.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
        ) : (
          <Campo label="Lote *">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Tipo *">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as TipoPlanItem)}>
              {TIPOS_PLAN_ITEM.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Nombre *"><input className={fieldCls} placeholder="Ej. Aftosa" value={nombre} onChange={(e) => setNombre(e.target.value)} /></Campo>
          <Campo label="Producto"><input className={fieldCls} value={producto} onChange={(e) => setProducto(e.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Dosis"><input className={fieldCls} value={dosis} onChange={(e) => setDosis(e.target.value)} /></Campo>
          <Campo label="Vía">
            <select className={fieldCls} value={via} onChange={(e) => setVia(e.target.value)}>
              <option value="">— Selecciona —</option>
              {VIAS_APLICACION.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Campo>
          <Campo label="Lote del producto"><input className={fieldCls} value={loteProducto} onChange={(e) => setLoteProducto(e.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Responsable"><input className={fieldCls} value={responsable} onChange={(e) => setResponsable(e.target.value)} /></Campo>
          <Campo label="Ítem de plan (opcional)">
            <select className={fieldCls} value={planItemId} onChange={(e) => setPlanItemId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {itemsActivos.map((it) => <option key={it.id} value={it.id}>{it.planNombre} · {it.nombre}</option>)}
            </select>
          </Campo>
        </div>
        <Campo label="Diagnóstico"><textarea className={`${fieldCls} h-16`} value={diagnostico} onChange={(e) => setDiagnostico(e.target.value)} /></Campo>
        <div className="grid grid-cols-4 gap-3">
          <Campo label="Retiro leche (días)"><input type="number" min="0" className={fieldCls} value={retiroLeche} onChange={(e) => setRetiroLeche(e.target.value)} /></Campo>
          <Campo label="Retiro carne (días)"><input type="number" min="0" className={fieldCls} value={retiroCarne} onChange={(e) => setRetiroCarne(e.target.value)} /></Campo>
          <Campo label="Costo ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={costo} onChange={(e) => setCosto(e.target.value)} /></Campo>
          <Campo label="Próximo refuerzo"><input type="date" className={fieldCls} value={proximoRefuerzo} onChange={(e) => setProximoRefuerzo(e.target.value)} /></Campo>
        </div>
        <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!puedeGuardar} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------- Planes
function PlanesTab({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [editId, setEditId] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const { data: planes = [], isFetching } = useQuery<PlanSanitario[]>({
    queryKey: ['gan-planes-sanitarios'], queryFn: () => ganRpc<PlanSanitario[]>('gan_plan_sanitario_listar'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => ganRpc('gan_plan_sanitario_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Plan eliminado'); invalidateGanaderia(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (editando) {
    return (
      <PlanEditor
        plan={planes.find((p) => p.id === editId) ?? null}
        onClose={() => { setEditando(false); setEditId(null); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex justify-end">
        <Button disabled={!admin} onClick={() => { setEditId(null); setEditando(true); }}><Plus className="w-4 h-4 mr-1" /> Nuevo plan</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Planes sanitarios ({planes.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Especie</th>
              <th className="py-2 pr-3 text-right">Ítems</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {planes.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin planes sanitarios registrados</td></tr>}
              {planes.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => { setEditId(p.id); setEditando(true); }}>
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.especie ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{p.items.length}</td>
                  <td className="py-2 pr-3">{p.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" disabled={!admin} onClick={() => { setEditId(p.id); setEditando(true); }}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" disabled={!admin}
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar el plan "${p.nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function PlanEditor({ plan, onClose }: { plan: PlanSanitario | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(plan?.nombre ?? '');
  const [especie, setEspecie] = useState(plan?.especie ?? 'bovino');
  const [activo, setActivo] = useState(plan?.activo ?? true);
  const [items, setItems] = useState<PlanItem[]>(plan?.items.map((it) => ({ ...it })) ?? []);

  const agregarItem = () => setItems((its) => [...its, {
    tipo: 'vacuna', nombre: '', producto: '', base_calculo: 'edad', edad_dias: null, intervalo_dias: null,
    obligatoria: true, retiro_leche_dias: 0, retiro_carne_dias: 0, orden: its.length + 1,
  }]);
  const quitarItem = (idx: number) => setItems((its) => its.filter((_, i) => i !== idx));
  const actualizarItem = (idx: number, patch: Partial<PlanItem>) =>
    setItems((its) => its.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const guardar = useMutation({
    mutationFn: () => ganRpc<PlanSanitario>('gan_plan_sanitario_guardar', {
      _data: {
        id: plan?.id || undefined, nombre, especie: especie || null, activo,
        items: items.map((it, i) => ({
          id: it.id || undefined, tipo: it.tipo, nombre: it.nombre, producto: it.producto || null,
          base_calculo: it.base_calculo,
          edad_dias: it.base_calculo === 'edad' ? (it.edad_dias == null ? null : Number(it.edad_dias)) : null,
          intervalo_dias: it.base_calculo === 'intervalo' ? (it.intervalo_dias == null ? null : Number(it.intervalo_dias)) : null,
          obligatoria: it.obligatoria,
          retiro_leche_dias: it.retiro_leche_dias == null ? 0 : Number(it.retiro_leche_dias),
          retiro_carne_dias: it.retiro_carne_dias == null ? 0 : Number(it.retiro_carne_dias),
          orden: i + 1,
        })),
      },
    }),
    onSuccess: () => { toast.success('Plan guardado'); invalidateGanaderia(qc); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{plan ? 'Editar plan sanitario' : 'Nuevo plan sanitario'}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Nombre *"><input className={fieldCls} value={nombre} onChange={(e) => setNombre(e.target.value)} /></Campo>
          <Campo label="Especie"><input className={fieldCls} value={especie ?? ''} onChange={(e) => setEspecie(e.target.value)} /></Campo>
          <Campo label="Estado"><div className="pt-2"><Switch checked={activo} onChange={setActivo} label={activo ? 'Activo' : 'Inactivo'} /></div></Campo>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">Ítems del plan</h4>
            <Button size="sm" variant="outline" onClick={agregarItem}><Plus className="w-4 h-4 mr-1" /> Agregar ítem</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2 text-right">N°</th><th className="py-2 pr-2">Tipo</th><th className="py-2 pr-2">Nombre</th>
                <th className="py-2 pr-2">Producto</th><th className="py-2 pr-2">Base</th><th className="py-2 pr-2 text-right">Días</th>
                <th className="py-2 pr-2 text-center">Obligatoria</th><th className="py-2 pr-2 text-right">Retiro leche</th>
                <th className="py-2 pr-2 text-right">Retiro carne</th><th className="py-2 pr-2"></th>
              </tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={10} className="py-4 text-center text-muted-foreground">Sin ítems — agrega uno</td></tr>}
                {items.map((it, i) => (
                  <tr key={it.id ?? `nuevo-${i}`} className="border-b last:border-0">
                    <td className="py-1 pr-2 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1 pr-2 min-w-[150px]">
                      <select className={fieldCls} value={it.tipo} onChange={(e) => actualizarItem(i, { tipo: e.target.value as TipoPlanItem })}>
                        {TIPOS_PLAN_ITEM.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                      </select>
                    </td>
                    <td className="py-1 pr-2 min-w-[140px]"><input className={fieldCls} value={it.nombre} onChange={(e) => actualizarItem(i, { nombre: e.target.value })} /></td>
                    <td className="py-1 pr-2 min-w-[140px]"><input className={fieldCls} value={it.producto ?? ''} onChange={(e) => actualizarItem(i, { producto: e.target.value })} /></td>
                    <td className="py-1 pr-2 min-w-[120px]">
                      <select className={fieldCls} value={it.base_calculo} onChange={(e) => actualizarItem(i, { base_calculo: e.target.value as BaseCalculo })}>
                        <option value="edad">Edad</option><option value="intervalo">Intervalo</option>
                      </select>
                    </td>
                    <td className="py-1 pr-2 text-right min-w-[90px]">
                      {it.base_calculo === 'edad' ? (
                        <input type="number" min="0" className={fieldCls} value={it.edad_dias ?? ''} onChange={(e) => actualizarItem(i, { edad_dias: e.target.value === '' ? null : Number(e.target.value) })} />
                      ) : (
                        <input type="number" min="0" className={fieldCls} value={it.intervalo_dias ?? ''} onChange={(e) => actualizarItem(i, { intervalo_dias: e.target.value === '' ? null : Number(e.target.value) })} />
                      )}
                    </td>
                    <td className="py-1 pr-2 text-center"><input type="checkbox" checked={it.obligatoria} onChange={(e) => actualizarItem(i, { obligatoria: e.target.checked })} /></td>
                    <td className="py-1 pr-2 text-right min-w-[80px]"><input type="number" min="0" className={fieldCls} value={it.retiro_leche_dias ?? 0} onChange={(e) => actualizarItem(i, { retiro_leche_dias: Number(e.target.value) })} /></td>
                    <td className="py-1 pr-2 text-right min-w-[80px]"><input type="number" min="0" className={fieldCls} value={it.retiro_carne_dias ?? 0} onChange={(e) => actualizarItem(i, { retiro_carne_dias: Number(e.target.value) })} /></td>
                    <td className="py-1 pr-2"><button onClick={() => quitarItem(i)} className="rounded p-1 hover:bg-muted text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!nombre.trim()} onClick={() => guardar.mutate()}>Guardar plan</Button>
        </div>
      </CardContent>
    </Card>
  );
}
